// 使用副資材一覧の行組み立てと一括反映・各種アクションをまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiFukushizaiListService(deps) {
    const {
        DeepCopy,
        DicVal,
        KAKOUSIJI_DETAIL_COPY_MODE,
        KAKOUSIJI_FUKUSHIZAI_LIST_FIELD_RULES,
        KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE,
        KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE,
        KAKOUSIJI_QUALITY_DISPLAY_PANEL_TYPE,
        KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE,
        KAKOUSIJI_UI_ACTION,
        buildKakousijiKnownColumnIds,
        columninfo,
        columns,
        entrydataRef,
        extractKakousijiMasterCode,
        extractKakousijiMasterName,
        flushKakousijiListReflectSourcePanels,
        fnc,
        getKakousijiGridRowsByType,
        isEmpty,
        isKakousijiEmptyGridRow,
        kakousijiBulkInputMap,
        normalizeKakousijiGroupNo,
        normalizeKakousijiText,
        normalizeKakousijiUpperColumnId,
        openTemporaryOrderTab,
        readKakousijiRowValueIgnoreCase,
        recalcKakousijiFukushizaiListCalcRow,
        resolveKakousijiGridPanelByType,
        resolveKakousijiSourceFieldName,
        runHinbanSoseiCalc,
        setKakousijiGridRows,
        syncKakousijiGridRowsFromApi
    } = deps;

    function normalizeKakousijiFukushizaiListTorkubun(value) {
        const next = normalizeKakousijiText(value);
        return next === '' ? '1' : next;
    }
    function buildKakousijiFukushizaiListKey(kind, code, torkubun, torcode, torsubcode) {
        return [normalizeKakousijiText(kind), normalizeKakousijiText(code), normalizeKakousijiFukushizaiListTorkubun(torkubun), normalizeKakousijiText(torcode), normalizeKakousijiText(torsubcode)].join('|');
    }
    function resolveKakousijiFukushizaiListKind(ruleKind, sourceField) {
        const fieldId = normalizeKakousijiUpperColumnId(sourceField);
        if (fieldId === 'SSDZ_KOMONOFUDACD' || fieldId === 'SSDZ_SAGEFUDASIZECD') return '12';
        if (/^SSDZ_BRANDNAMECD[1-3]$/.test(fieldId) || /^SSDZ_SONOTANAMECD[1-6]$/.test(fieldId)) return '13';
        return normalizeKakousijiText(ruleKind);
    }
    function readKakousijiItemGroupFromAnyRow(row) {
        const source = isEmpty(row) ? {} : row;
        const hasItemGroupKey = [
            'SSDZ_ITMCITEMCD', 'SSDQ_ITMCITEMCD',
            'SSDZ_FUKUSIZAICOLORGRP', 'SSDQ_GROUPNO', 'SSDQ_FUKUSIZAICOLORGRP',
            'item', 'itemCd', 'group', 'groupNo'
        ].some((key) => readKakousijiRowValueIgnoreCase(source, key) !== undefined || DicVal(source, key, undefined) !== undefined);
        if (!hasItemGroupKey) return { itemCd: '', itemName: '', groupNo: '', colorCd: '', hasItemGroupKey: false };
        const rawItem = readKakousijiRowValueIgnoreCase(source, 'SSDZ_ITMCITEMCD')
            ?? readKakousijiRowValueIgnoreCase(source, 'SSDQ_ITMCITEMCD')
            ?? DicVal(source, 'item', DicVal(source, 'itemCd', ''));
        const rawGroup = readKakousijiRowValueIgnoreCase(source, 'SSDZ_FUKUSIZAICOLORGRP')
            ?? readKakousijiRowValueIgnoreCase(source, 'SSDQ_GROUPNO')
            ?? readKakousijiRowValueIgnoreCase(source, 'SSDQ_FUKUSIZAICOLORGRP')
            ?? DicVal(source, 'group', DicVal(source, 'groupNo', ''));
        const rawColor = readKakousijiRowValueIgnoreCase(source, 'SSDZ_COLORCD')
            ?? readKakousijiRowValueIgnoreCase(source, 'SSDQ_COLORCD')
            ?? DicVal(source, 'color', DicVal(source, 'colorCd', ''));
        const itemCd = extractKakousijiMasterCode(rawItem) || normalizeKakousijiText(rawItem);
        // 名称は「そのフィールドのコードが対象アイテムと一致する場合」だけ採用する。
        // 一致確認なしに entrydata の PDT_ITMCITEMCD_nm を拾うと、
        // サブアイテム名(例: LCT)がアイテム名として混入する。
        const readMatchedItemName = (fieldId) => {
            const fieldRaw = readKakousijiRowValueIgnoreCase(source, fieldId);
            if (fieldRaw === undefined) return '';
            const fieldCode = extractKakousijiMasterCode(fieldRaw) || normalizeKakousijiText(fieldRaw);
            if (fieldCode === '' || fieldCode !== itemCd) return '';
            return resolveKakousijiSourceFieldName(source, fieldId, fieldRaw);
        };
        const itemName = extractKakousijiMasterName(rawItem)
            || readMatchedItemName('SSDZ_ITMCITEMCD')
            || readMatchedItemName('SSDQ_ITMCITEMCD');
        const groupNo = normalizeKakousijiGroupNo(rawGroup);
        const colorCd = normalizeKakousijiText(rawColor);
        return { itemCd, itemName, groupNo, colorCd, hasItemGroupKey: true };
    }
    function collectKakousijiFukushizaiSourceItems() {
        const sourceItems = [];
        const row = isEmpty(entrydataRef.current) ? null : entrydataRef.current;
        if (!isEmpty(row) && typeof row === 'object') {
            const torkubun = normalizeKakousijiFukushizaiListTorkubun(DicVal(row, 'SSDZ_TORKUBUN', ''));
            const torcode = normalizeKakousijiText(DicVal(row, 'SSDZ_TORCODE', ''));
            const torsubcode = normalizeKakousijiText(DicVal(row, 'SSDZ_TORSUBCODE', ''));
            KAKOUSIJI_FUKUSHIZAI_LIST_FIELD_RULES.forEach((rule, ruleIndex) => {
                (Array.isArray(rule.fields) ? rule.fields : []).forEach((field, fieldIndex) => {
                    const rawValue = DicVal(row, field, '');
                    const code = extractKakousijiMasterCode(rawValue);
                    if (code === '') return;
                    const sourceName = resolveKakousijiSourceFieldName(row, field, rawValue);
                    sourceItems.push({
                        saveKind: resolveKakousijiFukushizaiListKind(DicVal(rule, 'kind', ''), field),
                        code,
                        sourceName,
                        sortIndex: String(ruleIndex).padStart(4, '0') + '|000001|' + String(fieldIndex).padStart(4, '0'),
                        torkubun,
                        torcode,
                        torsubcode
                    });
                });
            });
        }
        // 品質表示幅(区分14)は一覧反映の対象外(2026-08 仕様変更、加工指示画面と同一仕様)。
        // 品質表示(118/119)行からのsource収集は行わない。
        return sourceItems.sort((a, b) => {
            const sortCompare = normalizeKakousijiText(DicVal(a, 'sortIndex', '')).localeCompare(normalizeKakousijiText(DicVal(b, 'sortIndex', '')), 'ja', { numeric: true, sensitivity: 'base' });
            if (sortCompare !== 0) return sortCompare;
            const aKey = buildKakousijiFukushizaiListKey(DicVal(a, 'saveKind', ''), DicVal(a, 'code', ''), DicVal(a, 'torkubun', ''), DicVal(a, 'torcode', ''), DicVal(a, 'torsubcode', ''));
            const bKey = buildKakousijiFukushizaiListKey(DicVal(b, 'saveKind', ''), DicVal(b, 'code', ''), DicVal(b, 'torkubun', ''), DicVal(b, 'torcode', ''), DicVal(b, 'torsubcode', ''));
            return aKey.localeCompare(bKey, 'ja', { numeric: true, sensitivity: 'base' });
        });
    }
    function buildKakousijiExistingFukushizaiListRowMap(existingRows) {
        const map = {};
        (Array.isArray(existingRows) ? existingRows : []).forEach((row, index) => {
            if (isEmpty(row) || typeof row !== 'object') return;
            const key = buildKakousijiFukushizaiListKey(
                readKakousijiRowValueIgnoreCase(row, 'SSDZM_FUSIZAIKBN'),
                readKakousijiRowValueIgnoreCase(row, 'SSDZM_FUZOKU_HINBAN'),
                readKakousijiRowValueIgnoreCase(row, 'SSDZM_TORKUBUN'),
                readKakousijiRowValueIgnoreCase(row, 'SSDZM_TORCODE'),
                readKakousijiRowValueIgnoreCase(row, 'SSDZM_TORSUBCODE')
            );
            if (key.replace(/\|/g, '') === '') return;
            map[key] = { row, index, key };
        });
        return map;
    }
    function buildKakousijiFukushizaiListRows(sourceItems, existingRows) {
        const existingMap = buildKakousijiExistingFukushizaiListRowMap(existingRows);
        const nextRows = [];
        const usedKeys = new Set();
        const kindOrderCounters = {};
        (Array.isArray(sourceItems) ? sourceItems : []).forEach((item) => {
            const key = buildKakousijiFukushizaiListKey(DicVal(item, 'saveKind', ''), DicVal(item, 'code', ''), DicVal(item, 'torkubun', ''), DicVal(item, 'torcode', ''), DicVal(item, 'torsubcode', ''));
            if (usedKeys.has(key)) return;
            usedKeys.add(key);
            const hit = DicVal(existingMap, key, null);
            const base = isEmpty(hit) ? {} : DeepCopy(hit.row);
            const saveKind = normalizeKakousijiText(DicVal(item, 'saveKind', ''));
            kindOrderCounters[saveKind] = (Number(kindOrderCounters[saveKind]) || 0) + 1;
            base.rowid = String(nextRows.length);
            base.SSDZM_FUSIZAIKBN = saveKind;
            base.SSDZM_FUZOKU_HINBAN = normalizeKakousijiText(DicVal(item, 'code', ''));
            base.SSDZM_FUZOKU_HINBAN_cd = normalizeKakousijiText(DicVal(item, 'code', ''));
            base.SSDZM_FUZOKU_HINBAN_nm = normalizeKakousijiText(DicVal(item, 'sourceName', DicVal(base, 'SSDZM_FUZOKU_HINBAN_nm', '')));
            base.SSDZM_TORKUBUN = normalizeKakousijiFukushizaiListTorkubun(DicVal(item, 'torkubun', '1'));
            base.SSDZM_TORCODE = normalizeKakousijiText(DicVal(item, 'torcode', ''));
            base.SSDZM_TORSUBCODE = normalizeKakousijiText(DicVal(item, 'torsubcode', ''));
            if (normalizeKakousijiText(DicVal(base, 'SSDZM_YOJAKUKOSU', '')) === '') base.SSDZM_YOJAKUKOSU = '1';
            base.SSDZM_HYOJIJUN = String(kindOrderCounters[saveKind]);
            nextRows.push(recalcKakousijiFukushizaiListCalcRow(base));
        });
        return nextRows;
    }
    function applyKakousijiBulkValueToFukushizaiList(targetColumnId, value) {
        const panelInfo = resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE);
        if (isEmpty(panelInfo)) return;
        const nextValue = normalizeKakousijiText(value);
        if (nextValue === '') return;
        const targetId = normalizeKakousijiUpperColumnId(targetColumnId);
        const targetColumns = buildKakousijiKnownColumnIds(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE);
        const currentRows = DeepCopy(getKakousijiGridRowsByType(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE));
        if (!Array.isArray(currentRows) || currentRows.length === 0) return;
        const nextRows = currentRows.map((row) => {
            const nextRow = isEmpty(row) ? {} : { ...row };
            if (isKakousijiEmptyGridRow(nextRow, targetColumns)) return nextRow;
            nextRow[targetId] = nextValue;
            return normalizeKakousijiUpperColumnId(targetColumnId) === 'SSDZM_LOSSRITU'
                ? recalcKakousijiFukushizaiListCalcRow(nextRow)
                : nextRow;
        });
        setKakousijiGridRows(panelInfo, nextRows);
    }
    function runKakousijiHeaderAction(action, panelType = KAKOUSIJI_QUALITY_DISPLAY_PANEL_TYPE) {
        if (action === KAKOUSIJI_UI_ACTION.PRODUCT_ORDER) {
            openTemporaryOrderTab();
            return;
        }
        if (action === KAKOUSIJI_UI_ACTION.CALC) {
            runHinbanSoseiCalc(panelType);
        }
    }
    function runKakousijiDetailAction(panelType, mode) {
        const typeKey = normalizeKakousijiText(panelType);
        if (mode === KAKOUSIJI_DETAIL_COPY_MODE.LIST_REFLECT) {
            if (typeKey === KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE) {
                handleKakousijiFukushizaiListReflect();
            }
            return;
        }
        if (mode === KAKOUSIJI_DETAIL_COPY_MODE.BULK_LOSS) {
            if (typeKey === KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE) {
                handleKakousijiSozaiBulkLossApply();
                return;
            }
            if (typeKey === KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE) {
                handleKakousijiFuzokuBulkLossApply();
                return;
            }
            if (typeKey === KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE) {
                handleKakousijiFukushizaiBulkLossApply();
            }
            return;
        }
        if (mode === KAKOUSIJI_DETAIL_COPY_MODE.BULK_SHIKYU) {
            if (typeKey === KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE) {
                handleKakousijiFuzokuBulkShikyuApply();
                return;
            }
            if (typeKey === KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE) {
                handleKakousijiFukushizaiBulkShikyuApply();
            }
        }
    }
    function handleKakousijiFukushizaiListReflect() {
        const panelInfo = resolveKakousijiGridPanelByType(KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE);
        if (isEmpty(panelInfo)) return;
        flushKakousijiListReflectSourcePanels();
        const sourceItems = collectKakousijiFukushizaiSourceItems();
        if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
            fnc.current.warnmsg('一覧反映の元データがありません');
            return;
        }
        const existingRows = DeepCopy(syncKakousijiGridRowsFromApi(panelInfo));
        const nextRows = buildKakousijiFukushizaiListRows(sourceItems, existingRows);
        setKakousijiGridRows(panelInfo, nextRows);
    }
    function handleKakousijiFukushizaiBulkLossApply() {
        const value = DicVal(DicVal(kakousijiBulkInputMap, KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE, {}), 'lossRate', '');
        applyKakousijiBulkValueToFukushizaiList('SSDZM_LOSSRITU', value);
    }
    function resolveKakousijiBulkTargetColumnId(panelType, kind) {
        const typeKey = normalizeKakousijiText(panelType);
        if (typeKey === '') return '';
        const panelColumns = [
            ...(Array.isArray(columninfo) ? columninfo : []),
            ...(Array.isArray(columns) ? columns : [])
        ].filter((col) => normalizeKakousijiText(DicVal(col, 'type', '')) === typeKey);
        const visibleIds = buildKakousijiKnownColumnIds(typeKey);

        const buildCandidates = (predicates) => {
            const ids = [];
            panelColumns.forEach((col) => {
                const columnId = normalizeKakousijiUpperColumnId(DicVal(col, 'columnid', DicVal(col, 'field', '')));
                if (columnId === '' || ids.includes(columnId)) return;
                const columnName = normalizeKakousijiText(DicVal(col, 'columnnm', DicVal(col, 'headerName', '')));
                if (!predicates.some((predicate) => predicate(columnId, columnName))) return;
                ids.push(columnId);
            });
            visibleIds.forEach((columnId) => {
                const normalizedId = normalizeKakousijiUpperColumnId(columnId);
                if (normalizedId === '' || ids.includes(normalizedId)) return;
                if (!predicates.some((predicate) => predicate(normalizedId, ''))) return;
                ids.push(normalizedId);
            });
            return ids;
        };

        if (kind === 'shikyu') {
            const candidates = buildCandidates([
                (columnId, columnName) => columnName.indexOf('支給区分') !== -1,
                (columnId) => /SIKYUKBN$/i.test(columnId),
                (columnId) => /SHIKYUKBN$/i.test(columnId)
            ]);
            return DicVal(candidates, 0, '');
        }

        const candidates = buildCandidates([
            (columnId, columnName) => columnName.indexOf('ロス率') !== -1,
            (columnId) => /LOSSRATE$/i.test(columnId),
            (columnId) => /LOSRITU$/i.test(columnId),
            (columnId) => /LOSS$/i.test(columnId)
        ]);
        return DicVal(candidates, 0, '');
    }
    function applyKakousijiBulkValueToDetailPanel(panelType, kind, value) {
        const panelInfo = resolveKakousijiGridPanelByType(panelType);
        if (isEmpty(panelInfo)) return;

        const nextValue = normalizeKakousijiText(value);
        if (nextValue === '') return;

        const targetColumnId = resolveKakousijiBulkTargetColumnId(panelType, kind);
        if (targetColumnId === '') {
            fnc.current.warnmsg(kind === 'shikyu' ? '支給区分の列が見つかりません' : 'ロス率の列が見つかりません');
            return;
        }

        const targetColumns = buildKakousijiKnownColumnIds(panelType);
        const currentRows = DeepCopy(getKakousijiGridRowsByType(panelType));
        if (!Array.isArray(currentRows) || currentRows.length === 0) return;

        const nextRows = currentRows.map((row) => {
            const nextRow = isEmpty(row) ? {} : { ...row };
            if (isKakousijiEmptyGridRow(nextRow, targetColumns)) return nextRow;
            nextRow[targetColumnId] = nextValue;
            return nextRow;
        });

        setKakousijiGridRows(panelInfo, nextRows);
    }
    function handleKakousijiSozaiBulkLossApply() {
        const value = DicVal(DicVal(kakousijiBulkInputMap, KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE, {}), 'lossRate', '');
        applyKakousijiBulkValueToDetailPanel(KAKOUSIJI_SOZAI_MEISAI_PANEL_TYPE, 'loss', value);
    }
    function handleKakousijiFuzokuBulkLossApply() {
        const value = DicVal(DicVal(kakousijiBulkInputMap, KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE, {}), 'lossRate', '');
        applyKakousijiBulkValueToDetailPanel(KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE, 'loss', value);
    }
    function handleKakousijiFuzokuBulkShikyuApply() {
        const value = DicVal(DicVal(kakousijiBulkInputMap, KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE, {}), 'shikyuKbn', '');
        applyKakousijiBulkValueToDetailPanel(KAKOUSIJI_FUZOKU_MEISAI_PANEL_TYPE, 'shikyu', value);
    }
    function handleKakousijiFukushizaiBulkShikyuApply() {
        const value = DicVal(DicVal(kakousijiBulkInputMap, KAKOUSIJI_FUKUSHIZAI_LIST_PANEL_TYPE, {}), 'shikyuKbn', '');
        applyKakousijiBulkValueToFukushizaiList('SSDZM_SIKYUKBN', value);
    }

    return {
        normalizeKakousijiFukushizaiListTorkubun,
        buildKakousijiFukushizaiListKey,
        resolveKakousijiFukushizaiListKind,
        readKakousijiItemGroupFromAnyRow,
        collectKakousijiFukushizaiSourceItems,
        buildKakousijiExistingFukushizaiListRowMap,
        buildKakousijiFukushizaiListRows,
        applyKakousijiBulkValueToFukushizaiList,
        runKakousijiHeaderAction,
        runKakousijiDetailAction,
        handleKakousijiFukushizaiListReflect,
        handleKakousijiFukushizaiBulkLossApply,
        resolveKakousijiBulkTargetColumnId,
        applyKakousijiBulkValueToDetailPanel,
        handleKakousijiSozaiBulkLossApply,
        handleKakousijiFuzokuBulkLossApply,
        handleKakousijiFuzokuBulkShikyuApply,
        handleKakousijiFukushizaiBulkShikyuApply
    };
}
